/**
 * Change History Types
 * Type definitions for entity change history tracking and diff comparison
 */

/**
 * Supported entity types for change tracking
 */
export enum ChangeEntityType {
  PRODUCT_LINE = 'product_line',
  RPN = 'rpn',
  FPN = 'fpn',
  BULK_EDIT = 'bulk_edit',
}

/**
 * The section/area of the entity that was changed
 */
export enum ChangeSectionName {
  OVERVIEW = 'overview',
  PROGRAM_STATUS = 'program_status',
  ATTRIBUTES = 'attributes',
  STANDARD_ATTRIBUTES = 'standard_attributes',
  PRODUCT_LINE_ATTRIBUTES = 'product_line_attributes',
  PROGRAM_ATTRIBUTES = 'program_attributes',
  COUNTRY_KEY_DATES = 'country_key_dates',
  STREET_DATES = 'street_dates',
  OTHER_KEY_DATES = 'other_key_dates',
  MANUFACTURING_INFO = 'manufacturing_info',
  MARKET_INFO = 'market_info',
  SPECIFICATIONS = 'specifications',
  FINANCIAL_INFO = 'financial_info',
  AGM_VERSIONS = 'agm_versions',
  AGM_ASSORTMENTS = 'agm_assortments',
  AGM_SKUS = 'agm_skus',
  DOCUMENTS_LINKS = 'documents_links',
}

/**
 * The type of change operation
 */
export enum ChangeOperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  BULK_EDIT = 'bulk_edit',
}

/**
 * Represents a single field-level change
 */
export interface FieldChange {
  /** Name of the field that changed */
  field_name: string;
  /** Human-readable label for the field */
  field_label: string;
  /** Previous value (null for create operations) */
  old_value: string | number | boolean | null;
  /** New value (null for delete operations) */
  new_value: string | number | boolean | null;
}

/**
 * Represents a single change history entry
 */
export interface ChangeHistoryEntry {
  /** Unique identifier for this change entry */
  change_id: string;
  /** ISO DateTime string when the change was made */
  created_at: string;
  /** User alias who made the change */
  created_by: string;
  /** Type of entity changed */
  entity_type: ChangeEntityType;
  /** ID of the entity that was changed */
  entity_id: number;
  /** Display name/alias of the entity */
  entity_name: string;
  /** Section of the entity that was changed */
  section: ChangeSectionName;
  /** Human-readable section label */
  section_label: string;
  /** Type of change operation */
  operation: ChangeOperationType;
  /** Human-readable summary of the change */
  summary: string;
  /** Detailed field-level changes */
  field_changes: FieldChange[];
  /** Number of records affected */
  record_count: number;
  /** Whether this change was part of a bulk operation */
  is_bulk_operation: boolean;
}

/**
 * Parameters for fetching change history
 */
export interface ChangeHistoryParams {
  /** Type of entity */
  entity_type: ChangeEntityType;
  /** ID of the entity */
  entity_id: number;
  /** Optional product line ID for context */
  product_line_id?: number;
  /** Optional RPN ID for context (when fetching FPN history) */
  rpn_id?: number;
  /** Optional section filter */
  section?: ChangeSectionName;
  /** Optional date range start */
  from_date?: string;
  /** Optional date range end */
  to_date?: string;
  /** Page size */
  limit?: number;
  /** Pagination offset */
  offset?: number;
}

/**
 * Response from the change history API
 */
export interface ChangeHistoryResponse {
  /** Array of change history entries */
  entries: ChangeHistoryEntry[];
  /** Total number of entries (for pagination) */
  total_count: number;
  /** Whether there are more entries to load */
  has_more: boolean;
}

/**
 * Redux state shape for change history
 */
export interface ChangeHistoryState {
  entriesByEntity: Record<string, ChangeHistoryEntry[]>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
  hasFetched: Record<string, boolean>;
}

/**
 * Mapping of section names to human-readable labels
 */
export const SECTION_LABELS: Record<ChangeSectionName, string> = {
  [ChangeSectionName.OVERVIEW]: 'Program Overview',
  [ChangeSectionName.PROGRAM_STATUS]: 'Program Status',
  [ChangeSectionName.ATTRIBUTES]: 'Attributes',
  [ChangeSectionName.STANDARD_ATTRIBUTES]: 'Standard Attributes',
  [ChangeSectionName.PRODUCT_LINE_ATTRIBUTES]: 'Product Line Attributes',
  [ChangeSectionName.PROGRAM_ATTRIBUTES]: 'Program Attributes',
  [ChangeSectionName.COUNTRY_KEY_DATES]: 'Country & Key Dates',
  [ChangeSectionName.STREET_DATES]: 'Street Dates',
  [ChangeSectionName.OTHER_KEY_DATES]: 'Other Key Dates',
  [ChangeSectionName.MANUFACTURING_INFO]: 'Manufacturing',
  [ChangeSectionName.MARKET_INFO]: 'Market',
  [ChangeSectionName.SPECIFICATIONS]: 'Specifications',
  [ChangeSectionName.FINANCIAL_INFO]: 'Financial Information',
  [ChangeSectionName.AGM_VERSIONS]: 'AGM Versions',
  [ChangeSectionName.AGM_ASSORTMENTS]: 'AGM Assortments',
  [ChangeSectionName.AGM_SKUS]: 'AGM SKUs',
  [ChangeSectionName.DOCUMENTS_LINKS]: 'Documents & Links',
};

/**
 * Mapping of operation types to human-readable labels
 */
export const OPERATION_LABELS: Record<ChangeOperationType, string> = {
  [ChangeOperationType.CREATE]: 'Created',
  [ChangeOperationType.UPDATE]: 'Updated',
  [ChangeOperationType.DELETE]: 'Deleted',
  [ChangeOperationType.BULK_EDIT]: 'Bulk Edited',
};
